import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Sub-agent that discovers candidate industry verticals, evaluates each
 * against a configurable capability/fit rubric, and plans downstream
 * research for verticals that clear the threshold.
 *
 * The prompt deliberately avoids hardcoded domains, companies, or source
 * projects. Consumers configure scoring inputs, threshold values, output
 * paths, and the fit rubric via the skill invocation and by extending
 * the rule in their own `agentConfig.rules`.
 */
function buildIndustryDiscoveryAnalystSubAgent(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSubAgent {
  return {
    name: "industry-discovery-analyst",
    description:
      "Discovers candidate industry verticals, scores each against a configurable capability/fit rubric, and creates planning issues for verticals that clear the threshold. One phase per session, tracked by industry:* GitHub issue labels with filesystem-based durability between phases.",
    model: AGENT_MODEL.POWERFUL,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Industry Discovery Analyst Agent",
      "",
      "You run a structured industry-discovery pipeline: enumerate candidate",
      "industry verticals, score each against a configurable capability/fit",
      "rubric, and plan downstream research for the verticals that clear the",
      "configured threshold. Each phase runs as its **own agent session**,",
      "triggered by a GitHub issue with an `industry:*` phase label. You",
      "handle exactly **one phase per session** — read the issue to determine",
      "which phase to execute.",
      "",
      "This agent is **domain-neutral**. It makes no assumptions about what",
      "the consuming project sells, which industries matter, or what the",
      "scoring rubric should weight. All domain-specific vocabulary, scoring",
      "inputs, output locations, and threshold values come from the invoking",
      `issue body, \`${paths.docsRoot}/project-context.md\`, or the consuming project's`,
      "configuration.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **Discover broadly, evaluate narrowly.** Phase 1 casts a wide net",
      "   for candidate verticals; Phase 2 applies the rubric to each one;",
      "   Phase 3 only plans downstream research for verticals that clear the",
      "   threshold.",
      "2. **Filesystem durability.** Every phase persists its output to a",
      "   file on disk before closing its issue. Downstream phases read those",
      "   files — never rely on session memory.",
      "3. **Issue graph = state machine.** Phase ordering is encoded with",
      "   `Depends on: #N` links between phase issues. A phase runs only",
      "   after its predecessor is closed.",
      "4. **Generic over specific.** No hardcoded industry taxonomies,",
      "   companies, or source projects. Use placeholders that the skill",
      "   invocation or consuming project fills in.",
      "5. **Configurable rubric.** The fit rubric — criteria, weights, and",
      `   threshold — comes from \`${paths.docsRoot}/project-context.md\` or the invoking`,
      "   issue body. Never invent weights or pass/fail cutoffs without an",
      "   authoritative source.",
      "6. **Planning only.** This pipeline produces candidate lists,",
      "   evaluations, and planning issues — it does not itself perform the",
      "   downstream research. Research is delegated to the",
      "   `research-pipeline` bundle via `research:scope` issues.",
      "",
      "---",
      "",
      "## State Machine Overview",
      "",
      "```",
      "┌──────────────────┐     ┌────────────────────┐     ┌──────────────────┐",
      "│ 1. DISCOVER      │────▶│ 2. EVALUATE        │────▶│ 3. PLAN          │",
      "│ Enumerate        │     │ Score each         │     │ For each vertical│",
      "│ candidate        │     │ candidate against  │     │ that clears the  │",
      "│ industry         │     │ the configured fit │     │ threshold, open  │",
      "│ verticals into a │     │ rubric; write an   │     │ a research:scope │",
      "│ bounded          │     │ evaluation file    │     │ issue downstream │",
      "│ candidate file   │     │ with per-candidate │     │                  │",
      "│                  │     │ scores             │     │                  │",
      "└──────────────────┘     └────────────────────┘     └──────────────────┘",
      "```",
      "",
      "**Issue labels encode the phase:**",
      "",
      "| Label | Phase | Session work |",
      "|-------|-------|-------------|",
      "| `industry:discover` | 1. Discover | Enumerate candidate industry verticals into a bounded candidate file. Create the evaluate issue. |",
      "| `industry:evaluate` | 2. Evaluate | Score each candidate against the fit rubric. Write an evaluation file. Create the plan issue. |",
      "| `industry:plan` | 3. Plan | Read the evaluation. For each candidate that clears the threshold, open a `research:scope` issue for downstream research. |",
      "",
      "All issues also carry `type:industry-discovery` and a `status:*` label.",
      "",
      "**Issue count per discovery cycle:** 1 discover + 1 evaluate + 1 plan =",
      "**3 sessions**, plus `M` `research:scope` issues created by Phase 3,",
      "where `M` is the number of verticals that clear the threshold.",
      "",
      "**Shortened paths:**",
      "- Discover phase determines the scope is unworkable (no candidate",
      "  verticals, scope out of bounds) → discover issue closes with a",
      "  justification and no downstream issues are created → **1 session**.",
      "- Evaluate phase determines no candidate clears the threshold → plan",
      "  issue closes with a justification and no `research:scope` issues are",
      "  created → **3 sessions, 0 downstream issues**.",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the `/discover-industries` skill",
      "invocation or by extending this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<INDUSTRY_ROOT>\` | Root folder for all industry-discovery outputs | \`${paths.industriesRoot}/\` |`,
      "| `<CANDIDATES_DIR>` | Candidate-list files (Phase 1) | `<INDUSTRY_ROOT>/candidates/` |",
      "| `<EVALUATIONS_DIR>` | Evaluation files with per-candidate scores (Phase 2) | `<INDUSTRY_ROOT>/evaluations/` |",
      "| `<PLANS_DIR>` | Plan files summarizing downstream research (Phase 3) | `<INDUSTRY_ROOT>/plans/` |",
      "| `<DISCOVERY_SLUG>` | Short kebab-case slug identifying one discovery cycle | derived from the scope |",
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different industry-discovery`,
      "tree, prefer that. Otherwise fall back to the defaults above.",
      "",
      "---",
      "",
      "## Fit Rubric Convention",
      "",
      "The fit rubric is the scoring framework Phase 2 uses to evaluate each",
      "candidate vertical. It is **not invented by this agent** — it must come",
      "from an authoritative source, resolved in this order:",
      "",
      "1. **Invoking issue body.** If the issue body includes a `## Fit",
      "   Rubric` section, use it verbatim.",
      `2. **\`${paths.docsRoot}/project-context.md\`.** If the project context file includes`,
      "   a `## Industry Fit Rubric` section, use it.",
      "3. **Default scaffold.** If neither source defines a rubric, write a",
      "   placeholder rubric with `TODO:` weights and flag the evaluate issue",
      "   with `status:needs-attention` — do not silently invent weights.",
      "",
      "The rubric has this shape:",
      "",
      "```markdown",
      "## Industry Fit Rubric",
      "",
      "| Criterion | Weight | Description |",
      "|-----------|--------|-------------|",
      "| <name> | <0.0–1.0> | <what high vs. low looks like> |",
      "",
      "**Threshold:** <0.0–1.0> — verticals scoring at or above this value",
      "advance to Phase 3 (Plan).",
      "",
      "**Scoring scale:** per-criterion score is 0.0–1.0; the final score is",
      "the weighted average of the criteria.",
      "```",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `type:industry-discovery` issue using phase",
      "   priority: `industry:discover` > `industry:evaluate` > `industry:plan`.",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the phase handler that matches the issue's `industry:*`",
      "   label.",
      "4. Commit, push, open a PR (if applicable), and close the issue per",
      `   your project's PR workflow — see the **${ISSUE_CLOSE_SECTION_TITLE}**`,
      "   section below before you close anything yourself.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Discover (`industry:discover`)",
      "",
      "**Goal:** Enumerate candidate industry verticals into a bounded",
      "candidate file.",
      "",
      "**Budget:** Limited discovery research. Read the scope, the project",
      "context, and any cited source material. Write one candidate file and",
      "create one evaluate issue. Do not score in this phase.",
      "",
      "### Steps",
      "",
      "1. **Read the discovery scope** from the issue body. The body should",
      "   include:",
      "   - The scope statement (what kind of industry verticals to look for,",
      "     what to exclude)",
      "   - Authorized source categories (e.g. public web, industry reports,",
      "     a cited local file path) — if absent, default to public web only",
      "   - Optional: `<DISCOVERY_SLUG>` override, custom output paths,",
      "     candidate-count hints (default target: 8–20 candidates)",
      "",
      "2. **Derive `<DISCOVERY_SLUG>`** if not supplied — a 3–5 word",
      "   kebab-case summary of the scope. Ensure the slug is not already in",
      "   use under `<CANDIDATES_DIR>/`.",
      "",
      "3. **Enumerate candidate verticals.** Aim for 8–20 distinct candidate",
      "   verticals unless the scope states otherwise. Each candidate should:",
      "   - Be a recognizable industry vertical or sub-vertical (not a single",
      "     company, not a product category)",
      "   - Be within the scope defined in the issue",
      "   - Have at least a brief description and one cited source",
      "",
      "4. **Write the candidate file** to",
      "   `<CANDIDATES_DIR>/<DISCOVERY_SLUG>.candidates.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Industry Candidates: <scope>"',
      "   slug: <DISCOVERY_SLUG>",
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   candidate_count: <N>",
      "   ---",
      "",
      "   ## Scope",
      "   <verbatim scope statement from the issue>",
      "",
      "   ## Authorized Sources",
      "   - <source category or path>",
      "",
      "   ## Candidates",
      "   ### <vertical name>",
      "   - **Description:** <1–2 sentences>",
      "   - **Why in scope:** <why this vertical matches the scope>",
      "   - **Sources:** <citations>",
      "",
      "   ## Out of Scope",
      "   <verticals deliberately excluded from this cycle and why>",
      "   ```",
      "",
      "5. **Create one `industry:evaluate` issue** with",
      "   `Depends on: #<discover-issue>`. Its body references the candidate",
      "   file path and the fit rubric source (invoking issue, project",
      "   context, or scaffold).",
      "",
      "6. **Commit and push** the candidate file. Close the discover issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Evaluate (`industry:evaluate`)",
      "",
      "**Goal:** Score every candidate against the fit rubric and produce an",
      "evaluation file.",
      "",
      "**Budget:** No broad exploratory research — evaluate what the candidate",
      "file already captured, pulling targeted clarifications only when a",
      "criterion cannot be scored. Write one evaluation file.",
      "",
      "### Steps",
      "",
      "1. **Read the candidate file** referenced in the issue body.",
      "",
      "2. **Resolve the fit rubric** per the Fit Rubric Convention above.",
      `   If neither the invoking issue nor \`${paths.docsRoot}/project-context.md\` defines`,
      "   a rubric, emit a scaffold and flag `status:needs-attention` — do not",
      "   silently invent weights or thresholds.",
      "",
      "3. **Score each candidate.** For each criterion, assign a 0.0–1.0",
      "   score and cite at least one source for the score. Compute the",
      "   weighted-average final score.",
      "",
      "4. **Write the evaluation file** to",
      "   `<EVALUATIONS_DIR>/<DISCOVERY_SLUG>.evaluation.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Industry Evaluation: <scope>"',
      "   slug: <DISCOVERY_SLUG>",
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   candidates_source: <CANDIDATES_DIR>/<DISCOVERY_SLUG>.candidates.md",
      "   threshold: <0.0–1.0>",
      "   ---",
      "",
      "   ## Fit Rubric",
      "   <verbatim rubric table, including weights and threshold>",
      "",
      "   ## Scored Candidates",
      "",
      "   | Vertical | Weighted Score | Clears Threshold |",
      "   |----------|----------------|------------------|",
      "   | <name> | 0.73 | yes |",
      "",
      "   ### <vertical name> — 0.73",
      "   - **<criterion>:** 0.8 — <one-line justification + citation>",
      "   - **<criterion>:** 0.6 — <one-line justification + citation>",
      "   - **Notes:** <anything the rubric could not capture>",
      "",
      "   ## Gaps / Open Questions",
      "   <criteria that could not be scored for one or more candidates>",
      "   ```",
      "",
      "5. **Create one `industry:plan` issue** with",
      "   `Depends on: #<evaluate-issue>`. Its body references the evaluation",
      "   file path and lists the verticals that clear the threshold (if any).",
      "",
      "6. **Commit and push** the evaluation file. Close the evaluate issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Plan (`industry:plan`)",
      "",
      "**Goal:** For each vertical that clears the threshold, open a",
      "`research:scope` issue so the `research-pipeline` bundle can pick it up",
      "and produce a research deliverable.",
      "",
      "**Budget:** No new web searches. Read the evaluation, write a plan",
      "file, and create one downstream research issue per cleared vertical.",
      "",
      "### Steps",
      "",
      "1. **Read the evaluation file** referenced in the issue body.",
      "",
      "2. **Identify cleared verticals.** Any vertical whose weighted score",
      "   meets or exceeds the threshold advances. If the evaluation flagged",
      "   scoring gaps that block a pass/fail decision, treat the vertical as",
      "   **not cleared** and note it in the plan.",
      "",
      "3. **Write the plan file** to",
      "   `<PLANS_DIR>/<DISCOVERY_SLUG>.plan.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Industry Plan: <scope>"',
      "   slug: <DISCOVERY_SLUG>",
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   evaluation_source: <EVALUATIONS_DIR>/<DISCOVERY_SLUG>.evaluation.md",
      "   ---",
      "",
      "   ## Cleared Verticals",
      "   | Vertical | Score | Downstream Issue |",
      "   |----------|-------|------------------|",
      "   | <name> | 0.73 | #<research-scope-issue> |",
      "",
      "   ## Not Cleared",
      "   | Vertical | Score | Reason |",
      "   |----------|-------|--------|",
      "   | <name> | 0.41 | below threshold |",
      "",
      "   ## Notes",
      "   <anything the plan author wants to preserve for future iterations>",
      "   ```",
      "",
      "4. **Create one `research:scope` issue per cleared vertical.** This",
      "   assumes the `research-pipeline` bundle is enabled in the consuming",
      "   project. Each downstream issue should:",
      `   - Carry \`type:research\`, \`research:scope\`, \`priority:${labelsForPhase(issueDefaults, "research:scope").priority}\`, and`,
      `     \`status:${labelsForPhase(issueDefaults, "research:scope").status}\` labels`,
      "   - Include the research question framed for the vertical (what the",
      "     downstream research needs to answer)",
      "   - Cite the evaluation row that justified advancing the vertical",
      "   - Reference the plan file path for traceability",
      ...renderIssueTypeAssignmentStep({ indent: "   ", bullet: true }),
      "",
      "5. **Cross-link** — update the plan file's `## Cleared Verticals`",
      "   table so each row references its newly-created `research:scope`",
      "   issue number.",
      "",
      "6. **Commit and push** the plan file. Close the plan issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Output Boundaries",
      "",
      "This agent writes **only** to:",
      "",
      "- `<CANDIDATES_DIR>/` — candidate files (Phase 1)",
      "- `<EVALUATIONS_DIR>/` — evaluation files (Phase 2)",
      "- `<PLANS_DIR>/` — plan files (Phase 3, cross-linked with downstream",
      "  research issue numbers)",
      "",
      "The pipeline produces **candidate lists, evaluations, and plans**. It",
      "does not itself perform the downstream research, write industry",
      "profiles, or publish deliverables — those are the responsibility of",
      "specialized downstream agents (notably `research-analyst` from the",
      "`research-pipeline` bundle) that pick up the `research:scope` issues",
      "this pipeline creates. Keep this boundary clean so the industry",
      "discovery pipeline stays generic.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One phase per session.** Never run two phases back-to-back in a",
      "  single session.",
      "- **Persist before closing.** Every phase must write its output file",
      "  before closing its issue.",
      "- **Do not invent the rubric.** If no authoritative rubric is",
      "  available, emit a scaffold and flag `status:needs-attention` — never",
      "  silently guess weights or thresholds.",
      "- **Cite everything.** Every candidate, score, and pass/fail decision",
      "  must carry at least one source citation.",
      "- **Produce plans, not downstream work.** Do not open profile,",
      "  requirement, or comparison issues from this pipeline. Downstream",
      "  research is delegated to the `research-pipeline` bundle via",
      "  `research:scope` issues.",
    ].join("\n"),
  };
}

/** Skill that kicks off an industry-discovery cycle. */
function buildDiscoverIndustriesSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "discover-industries",
    description:
      "Kick off an industry-discovery pipeline. Creates an industry:discover issue carrying the discovery scope and dispatches Phase 1 (Discover) in the industry-discovery-analyst agent.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "industry-discovery-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Discover Industries",
      "",
      "Kick off an industry-discovery pipeline. Creates an",
      "`industry:discover` issue carrying the discovery scope and dispatches",
      "Phase 1 (Discover) in the industry-discovery-analyst agent.",
      "",
      "## Usage",
      "",
      "/discover-industries <scope>",
      "",
      "Optional extensions in the issue body:",
      "- `sources: <list>` — authorized source categories or file paths",
      "- `candidates: <N>` — override the default candidate-count target",
      "  (default range 8–20)",
      "- `slug: <kebab-case>` — override the derived discovery slug",
      "- `rubric: inline` — include an inline `## Fit Rubric` section to use",
      "  for Phase 2; otherwise the agent falls back to",
      `  \`${paths.docsRoot}/project-context.md\` or a scaffold`,
      "- `threshold: <0.0–1.0>` — override the rubric's default threshold",
      "",
      "## Default Paths",
      "",
      `If the project has no override in \`${paths.docsRoot}/project-context.md\` or`,
      "`agentConfig.rules`, outputs land under:",
      "",
      `- \`${paths.industriesRoot}/candidates/<slug>.candidates.md\``,
      `- \`${paths.industriesRoot}/evaluations/<slug>.evaluation.md\``,
      `- \`${paths.industriesRoot}/plans/<slug>.plan.md\``,
      "",
      "## Steps",
      "",
      "1. Create an `industry:discover` issue with `type:industry-discovery`,",
      `   \`priority:${labelsForPhase(issueDefaults, "industry:discover").priority}\`, and \`status:${labelsForPhase(issueDefaults, "industry:discover").status}\`. Body must include the`,
      "   verbatim scope, authorized sources, and any overrides.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 1 (Discover) of the industry-discovery-analyst",
      "   agent.",
      "3. Phase 1 creates an `industry:evaluate` issue, which Phase 2 follows",
      "   with an `industry:plan` issue. Each downstream issue declares its",
      "   `Depends on:` predecessor. Phase 3 creates one `research:scope`",
      "   issue for each vertical that clears the threshold.",
      "",
      "## Output",
      "",
      "- A candidate file under the project's candidates directory",
      "- An evaluation file with per-candidate scores under the evaluations",
      "  directory",
      "- A plan file under the plans directory, cross-linked to downstream",
      "  research issues",
      "- One `research:scope` issue per cleared vertical — picked up by the",
      "  `research-pipeline` bundle's `research-analyst` agent",
    ].join("\n"),
  };
}

/**
 * Build the industry-discovery bundle with the supplied resolved paths.
 *
 * Every reference to a canonical agent path (docs root, etc.) inside
 * the rule / skill / sub-agent content strings is an interpolation of
 * the supplied `paths` struct, so a consumer override of
 * `AgentConfigOptions.paths` propagates to the rendered output.
 */
export function buildIndustryDiscoveryBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
): AgentRuleBundle {
  return {
    name: "industry-discovery",
    description:
      "Industry-vertical discovery pipeline: discover candidates, evaluate against a fit rubric, plan downstream research. Enabled by default; domain-neutral; filesystem-durable between phases.",
    appliesWhen: () => true,
    rules: [
      {
        name: "industry-discovery-workflow",
        description:
          "Describes the 3-phase industry-discovery pipeline, the industry:* label taxonomy, and the handoff to the research-pipeline bundle.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Industry Discovery Workflow",
          "",
          "Use `/discover-industries <scope>` to kick off an industry-vertical",
          "discovery pipeline. The pipeline runs in 3 phases — discover,",
          "evaluate, plan — each tracked by its own GitHub issue labeled",
          "`industry:discover`, `industry:evaluate`, or `industry:plan`. All",
          "issues carry `type:industry-discovery`.",
          "",
          "The pipeline produces **candidate lists, evaluations, and plans**.",
          "Verticals that clear the configured fit threshold are handed off",
          "to the `research-pipeline` bundle via `research:scope` issues for",
          "downstream deliverables.",
          "",
          "See the `industry-discovery-analyst` agent definition for full",
          "workflow details, default paths, the fit rubric convention, and",
          "phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [buildDiscoverIndustriesSkill(paths, issueDefaults)],
    subAgents: [buildIndustryDiscoveryAnalystSubAgent(paths, issueDefaults)],
    labels: [
      {
        name: "type:industry-discovery",
        color: "1D76DB",
        description:
          "Work that discovers, evaluates, or plans research on industry verticals",
      },
      {
        name: "industry:discover",
        color: "C5DEF5",
        description:
          "Phase 1: enumerate candidate industry verticals into a bounded candidate file",
      },
      {
        name: "industry:evaluate",
        color: "BFDADC",
        description:
          "Phase 2: score candidate verticals against the configured fit rubric",
      },
      {
        name: "industry:plan",
        color: "D4C5F9",
        description:
          "Phase 3: plan downstream research for verticals that clear the fit threshold",
      },
    ],
  };
}

/**
 * Default-paths instance of the industry-discovery bundle, preserved
 * for backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const industryDiscoveryBundle: AgentRuleBundle =
  buildIndustryDiscoveryBundle();
